import PasswordValidator from "password-validator";
import React, { useEffect } from "react";
import "./PasswordPolicy.scss";
import { SeemPopover } from "@SeemplicityDev/shared";
import { useTranslation } from "react-i18next";

const schema = new PasswordValidator();

interface PasswordPolicyItemProps {
  isValid: boolean;
  text: string;
  index: number;
}

const PasswordPolicyItem: React.FC<PasswordPolicyItemProps> = ({
  isValid,
  text,
  index,
}) => {
  const { t } = useTranslation();

  return (
    <div
      key={`password-policy-rule-${index}`}
      className={`password-rule-row ${isValid ? "valid" : ""}`}
    >
      <i
        className={`seem-icon ${
          isValid ? "seem-icon-checkmark" : "seem-icon-x-small"
        }`}
      />
      <span className={"password-policy-text"}>{t(text)}</span>
    </div>
  );
};
schema
  .is()
  .min(8)
  .has()
  .digits(1)
  .has()
  .uppercase(1)
  .has()
  .lowercase(1)
  .has()
  .symbols(1);

interface PasswordPolicyProps {
  password: string;
  onPasswordValidate?: (isValid: boolean) => void;
}

const PasswordPolicy: React.FC<PasswordPolicyProps> = ({
  password,
  onPasswordValidate = () => {},
}) => {
  // `validate` with `{ list: true }` returns the array of failed rule names.
  const failedRulesList = schema.validate(password, {
    list: true,
  }) as string[];
  const isPasswordValid = !failedRulesList.length;

  useEffect(() => {
    onPasswordValidate(isPasswordValid);
  }, [isPasswordValid, onPasswordValidate]);

  const passwordPolicyRules = [
    {
      name: "min",
      message: "At least 8 characters",
    },
    {
      name: "digits",
      message: "At least 1 number",
    },
    {
      name: "uppercase",
      message: "At least 1 uppercase letter",
    },
    {
      name: "lowercase",
      message: "At least 1 lowercase letter",
    },
    {
      name: "symbols",
      message: "At least 1 special character",
    },
  ];

  return (
    <SeemPopover
      className={"password-policy-popover"}
      isOpen={!isPasswordValid}
      placement={"right"}
      body={
        <div key={`password-policy-rule`} className={`password-policy-wrapper`}>
          <h2 className="password-policy-title weight-normal">
            Password should contain
          </h2>
          {passwordPolicyRules.map((rule, index) => (
            <PasswordPolicyItem
              index={index}
              key={rule.name}
              isValid={!failedRulesList.includes(rule.name)}
              text={rule.message}
            />
          ))}
        </div>
      }
    >
      <div
        key={`error-message-tip-holder`}
        className={`error-message-tip-holder`}
      />
    </SeemPopover>
  );
};

export default PasswordPolicy;
